import React, { useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import Hls from 'hls.js';

/* UTILS */
import { rootUrl, baseName } from '../../utils/string';

const VideoPlayer = ({ src, ...props }) => {
	const videoRef = useRef(null);
	const hlsRef = useRef(null);
	const attachedRef = useRef(false); // prevents double-attach in StrictMode/hot reload

	useEffect(() => {
		const video = videoRef.current;
		if (!video || !src) return;

		let cancelled = false;

		const destroyHls = () => {
			if (hlsRef.current) {
				try { hlsRef.current.destroy(); } catch {}
				hlsRef.current = null;
			}
		};

		const resetVideo = () => {
			if (!video) return;
			// Clear src to stop any ongoing network activity
			video.removeAttribute('src');
			try { video.load(); } catch {}
			attachedRef.current = false;
		};

		const attachNative = (url) => {
			destroyHls();
			if (!attachedRef.current) {
				video.src = url;
				try { video.load(); } catch {}
				attachedRef.current = true;
			}
		};

		const init = async () => {
			// Prefer native HLS where available (Safari/iOS)
			if (video.canPlayType && video.canPlayType('application/vnd.apple.mpegurl')) {
				const playbackUrl = `${rootUrl(src)}/hls-${baseName(src).replace(/\.(mp4|mov)/, '/master.m3u8')}`;
				attachNative(playbackUrl);
				return;
			}

			// If hls.js is not supported, fallback to MP4 directly
			if (!Hls.isSupported()) {
				attachNative(src);
				return;
			}

			// Optional: your HEAD check to decide optimized vs MP4
			let playbackUrl = `${rootUrl(src)}/hls-${baseName(src).replace(/\.(mp4|mov)/, '/master.m3u8')}`;
			try {
				const head = await fetch(playbackUrl, { method: 'HEAD' });
				const isOptimized = head.ok && head.headers.get('x-optimized-version') === 'HLS';
				if (!isOptimized) playbackUrl = src;
			} catch {
				playbackUrl = src;
			}
			if (cancelled) return;

			// If we decided to use MP4, just attach natively
			if (!playbackUrl.endsWith('.m3u8')) {
				attachNative(playbackUrl);
				return;
			}

			// Guard against duplicate inits
			if (hlsRef.current) return;

			const hls = new Hls({
				// tune as needed:
				// enableWorker: true,
				// lowLatencyMode: false,
				// backBufferLength: 90,
			});
			hlsRef.current = hls;

			hls.attachMedia(video);
			hls.on(Hls.Events.MEDIA_ATTACHED, () => {
				hls.loadSource(playbackUrl);
			});

			hls.on(Hls.Events.ERROR, (_evt, data) => {
				if (data.fatal) {
					switch (data.type) {
						case Hls.ErrorTypes.NETWORK_ERROR:
							hls.startLoad(); // try resume
							break;
						case Hls.ErrorTypes.MEDIA_ERROR:
							hls.recoverMediaError();
							break;
						default:
							destroyHls();
							// fallback to MP4 if available
							attachNative(src);
					}
				}
			});
		};

		// Start
		resetVideo(); // ensure clean slate before attaching
		init();

		// Cleanup on unmount or src change
		// eslint-disable-next-line consistent-return
		return () => {
			cancelled = true;
			destroyHls();
			resetVideo();
		};
	}, [src]);

	return (
		<video ref={videoRef} {...props}>{/* eslint-disable-line jsx-a11y/media-has-caption */}
			Your browser does not support the video tag.
		</video>
	);
};

export default hot(module)(VideoPlayer);
